import * as React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Icon, Toast, ActivityIndicator } from 'zarm';
import { Upload } from '@iyunbao/components';

import UItem from '../item';
import { deepAsset, getFormId } from '../../utils/tools';
import { FetchProps, BaseWidgetProps } from '../../utils/type';

export interface UploadProps extends BaseWidgetProps {
  value?: any;
  defaultValue?: any;
  label?: string;
  type: 'file' | 'picture';
  accept?: string;
  multiple?: boolean;
  fetch?: FetchProps;
  quality?: number;
  min?: number;
  max?: number;
  readonly?: boolean;
  className?: string;
  validate?: {
    rules: any[];
  };
  timeout?: number;
}

interface UploadState {
  files: any;
  prevFiles?: any;
}
interface FileProps {
  file: any;
  fileType: string;
  fileSize: number;
  fileName: any;
  filename: any;
  url?: string;
  thumbnail: string;
  isError?: boolean;
}

const getValue = (props: UploadProps) => {
  const { defaultValue, value } = props;

  if (!(value && value.length) && typeof defaultValue !== 'undefined') {
    return defaultValue;
  }

  return (value || []);
};

class UploadFile extends React.PureComponent<UploadProps, UploadState> {
  static propTypes = {
    prefixCls: PropTypes.string,
    onChange: PropTypes.func,
  };

  static defaultProps = {
    prefixCls: 'crm-upload',
    onChange: () => {},
  };

  state: UploadState = {
    files: getValue(this.props),
  };

  static getDerivedStateFromProps(nextProps: UploadProps, state: UploadState) {
    if ('value' in nextProps && !deepAsset(nextProps.value, state.prevFiles)) {
      return {
        files: getValue(nextProps),
        prevFiles: nextProps.value,
      };
    }

    return null;
  }

  onSelect(file: FileProps) {
    const { files } = this.state;
    const { max } = this.props;

    let awaitFile: any[] = [];
    let len = files.length;

    // 多选
    if (Array.isArray(file)) {
      awaitFile = [...file];
      len += file.length;
    } else {
      awaitFile.push(file);
      len += 1;
    }

    if (max && len > max) {
      Toast.show({
        content: `最多只能上传${max}份!`,
        mask: true,
      });
      return false;
    }

    this.setState({
      files: [...files, ...awaitFile],
    });

    awaitFile.forEach((item, index) => {
      this.uploadFile(item, files.length + index);
    });
  }

  onRemove(index: number) {
    let { files } = this.state;

    if (files && Array.isArray(files) && files.length > 1) {
      files.splice(index, 1);
    } else {
      files = '';
    }

    Toast.show({
      content: '删除成功',
      mask: true,
    });

    this.setState({ files });

    this.forceUpdate();

    this.onChange(files);
  }

  // 上传失败，重新上传
  onReUpload(file: FileProps, index: number) {
    const { files } = this.state;

    files[index].isError = false;

    this.setState({ files });

    this.uploadFile(file, index);
  }

  onChange(files: FileProps[]) {
    const { onChange, ...rest } = this.props;
    const { _crengine } = this.props;
    const { path, dataBind, form } = _crengine;

    rest.value = files;

    // 重置 form value
    if (form && dataBind) {
      form.setFieldsValue({
        [getFormId(dataBind, path)]: files,
      });
    }

    if (onChange) {
      onChange(rest);
    }
  }

  // 上传文件
  uploadFile(file: FileProps, index: number) {
    const { files } = this.state;
    const { fetch: fetchData, timeout = 20000 } = this.props;
    const fetchConfig = {
      url: 'https://api.baoinsurance.com/facilities/open/v1/oss/file/upload/byBase64',
      method: 'post',
      data: {},
      ...fetchData,
    };

    const formData = new FormData();
    const suffix: string = (file.fileName ? file.fileName.split('.').pop() : '.jpg');

    formData.append('fileName', file.fileName);
    formData.append('suffix', suffix);
    formData.append('base64Val', file.thumbnail);

    // 附加参数
    if (fetchConfig.data && Object.keys(fetchConfig.data).length) {
      Object.keys(fetchConfig.data).forEach((key) => {
        if (key && fetchConfig.data[key]) {
          formData.append(key, fetchConfig.data[key]);
        }
      });
    }

    const abortController = new AbortController();
    const { signal } = abortController;
    const timer = setTimeout(() => {
      abortController.abort();
    }, timeout);

    // 游览器默认自带fetch
    window.fetch(fetchConfig.url, {
      method: fetchConfig.method,
      body: formData,
      signal,
    })
      .then((res) => res.json())
      .then(({ isSuccess, result = {}, errorMsg }) => {
        clearTimeout(timer);
        // 文件上传地址是C:filePath
        if (!(result.url && /^(https?:\/\/)/.test(result.url))) {
          return Toast.show(`上传地址不正确，请重新上传-${result.url}`);
        }
        
        if (isSuccess) {
          files[index] = {
            ...files[index],
            ...result,
            isError: false,
          };

          if (files[index] && files[index].thumbnail) {
            delete files[index].thumbnail;
          }

          this.setState({ files });
          this.forceUpdate();
          this.onChange(files);
        } else {
          files[index].isError = true;
          this.setState({ files });
          this.onChange(files);

          if (errorMsg) {
            Toast.show({
              content: errorMsg,
              mask: true,
            });
          }
        }
      })
      .catch(() => {
        files[index].isError = true;
        clearTimeout(timer);
        this.setState({ files });
        this.onChange(files);
      });
  }

  renderFileContent(fileName: string, url: string, isFile?: boolean) {
    const { prefixCls } = this.props;

    if (isFile) {
      return (
        <div className={`${prefixCls}-item-file`}>
          {fileName}
        </div>
      );
    }

    return (
      <div className={`${prefixCls}-item-pic`}>
        <img src={url} alt={fileName} />
      </div>
    );
  }

  renderFile(file: FileProps, index: number) {
    const { prefixCls, type, readonly } = this.props;
    const { url, thumbnail, fileName, filename, isError } = file;
    const isFile = !!((type && type === 'file'));

    const name = fileName || filename;

    // 上传成功
    if (url) {
      return (
        <div
          className={`${prefixCls}-item`}
          key={`item_${index}`}
        >
          {this.renderFileContent(name, url, isFile)}
          {!readonly && <i className={`${prefixCls}-close`} onClick={() => this.onRemove(index)} />}
        </div>
      );
    } else if (isError) {
      return (
        <div
          className={`${prefixCls}-item ${prefixCls}-item-hidden`}
          key={`item_${index}`}
          onClick={() => this.onReUpload(file, index)}
        >
          {this.renderFileContent(fileName, thumbnail, isFile)}
          {!readonly && <i className={`${prefixCls}-close`} onClick={() => this.onRemove(index)} />}
          <div className={`${prefixCls}-item-mask`}>
            上传失败<br />
            点击重新上传
          </div>
        </div>
      );
    } else {
      return (
        <div
          className={`${prefixCls}-item ${prefixCls}-item-hidden`}
          key={`item_${index}`}
        >
          {this.renderFileContent(fileName, thumbnail, isFile)}
          <div className={`${prefixCls}-item-mask`}>
            <ActivityIndicator className={`${prefixCls}-item-rotate`} />
          </div>
        </div>
      );
    }

   
  }

  renderItems() {
    const { files } = this.state;

    if (!(files && Array.isArray(files) && files.length)) {
      return null;
    }

    return files.map((item, index) => this.renderFile(item, index));
  }

  renderContent() {
    const { files } = this.state;
    const {
      prefixCls,
      type = 'picture',
      accept = 'image/*',
      multiple = false,
      quality = 0.8,
      min,
      max = 10,
      className,
      validate,
      readonly,
      _crengine,
      timeout,
      ...others
    } = this.props;
    const cls = classnames(prefixCls, className);

    return (
      <div className={cls}>
        {this.renderItems()}
        {
          (!readonly && files.length < max) && (
            <Upload
              {...others}
              className={`${prefixCls}-file`}
              accept={accept}
              quality={quality}
              multiple={multiple}
              onChange={(file: FileProps) => this.onSelect(file)}
            >
              <Icon type="add" size="lg" />
            </Upload>
          )
        }

      </div>
    );
  }

  render() {
    const { onChange, defaultValue=[], ...rest } = this.props;

    return (
      <UItem
        initialValue={defaultValue}
        itemType="upload"
        {...rest}
      >
        {
          this.renderContent()
        }
      </UItem>
    );
  }
}

export default UploadFile;
